<template>
  <v-dialog
    :value="isConfigurarTipoAgendamentoDialogOpen"
    width="900px"
    persistent
  >
    <v-card class="pa-sm-8 pa-2">
      <btn-close-dialog :handle-click="resetDataAndCloseDialog" />

      <v-row>
        <v-col
          cols="12"
          class="py-0"
        >
          <h2 class="text-center justify-center mb-4 mt-4">
            {{ isTipoAgendamentoNovo ? 'CADASTRAR TIPO DE AGENDAMENTO' : 'EDITAR TIPO DE AGENDAMENTO' }}
          </h2>
        </v-col>
        <v-col
          cols="12"
          class="py-1"
        >
          <v-divider class="mx-5"></v-divider>
        </v-col>
      </v-row>

      <v-card-text class="mt-5">
        <v-form
          ref="formRef"
          class="multi-col-validation"
          @submit.prevent="handleSubmit"
        >
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="tipoAgendamentoDataLocale.nome"
                dense
                outlined
                label="Nome"
                validate-on-blur
                hint="Máximo de 100 caracteres"
                :rules="[validators.required, v => (!!v && v.length <= 100) || 'Máximo de 100 caracteres']"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-select
                v-model="tipoAgendamentoDataLocale.filaId"
                class="mb-6"
                dense
                outlined
                label="Filas"
                placeholder="Filas"
                hide-details
                item-text="nome"
                item-value="id"
                :items="filas"
                :rules="[validators.select]"
              />
            </v-col>

            <v-col
              cols="12"
              sm="6"
            >
              <div class="d-flex align-center">
                <v-text-field
                  v-model.number="tipoAgendamentoDataLocale.tempoDeAtendimento"
                  v-mask="'###'"
                  dense
                  outlined
                  label="Tempo de Atendimento"
                  validate-on-blur
                  :rules="[v => (v >= 10 && v <= 480) || 'Tempo deve ser entre 10 e 480 minutos']"
                  class="mr-2"
                />
                <span>Minutos</span>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="tipoAgendamentoDataLocale.descricao"
                dense
                outlined
                label="Descricao"
                hint="Máximo de 300 caracteres"
                validate-on-blur
              />
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col
              cols="12"
              class="d-flex justify-center"
            >
              <v-btn
                color="primary"
                type="submit"
                :disabled="loading"
                class="mr-4"
              >
                <v-progress-circular
                  v-if="loading"
                  class="mx-3"
                  indeterminate
                  color="secondary"
                />
                <span v-else>Salvar</span>
              </v-btn>

              <v-btn
                color="secondary"
                outlined
                :disabled="loading"
                @click="resetDataAndCloseDialog"
              >
                Cancelar
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { computed, ref, watchEffect, provide, onMounted } from '@vue/composition-api'
import { required, select } from '@core/utils/validation'
import BtnCloseDialog from '@/components/btn-close-dialog/BtnCloseDialog.vue'
import useDialog from '@/composables/useDialog'
import TipoAgendamentoModel from '@/models/entities/agenda/TipoAgendamentoModel'
import TipoAgendamentoService from '@/services/modules/TicketAgendamentoTipoService'
import FilaService from '@/services/modules/FilaService'

export default {
  components: {
    BtnCloseDialog,
  },

  props: {
    isConfigurarTipoAgendamentoDialogOpen: {
      type: Boolean,
      default: null,
    },
    tipoAgendamentoId: {
      type: Number,
      required: true,
    },
    closeDialog: {
      type: Function,
      required: true,
    },
  },

  setup(props, { emit }) {
    const { formRef, loading, resetForm } = useDialog(emit, 'is-configurar-tipo-agendamento-dialog-open')
    const tipoAgendamentoDataLocale = ref(new TipoAgendamentoModel())

    const filas = ref([])

    const isTipoAgendamentoNovo = computed(() => props.tipoAgendamentoId === 0)

    async function getFilas() {
      filas.value = await FilaService.getFilasDisponiveisParaAgendamento()
    }

    async function getTipoAgendamento() {
      try {
        loading.value = true
        tipoAgendamentoDataLocale.value = await TipoAgendamentoService.getTipoAgendamentoById(props.tipoAgendamentoId)
      } finally {
        loading.value = false
      }
    }

    watchEffect(() => {
      if (props.tipoAgendamentoId) {
        getTipoAgendamento()
      }
    })

    onMounted(() => {
      getFilas()
    })

    function resetData() {
      resetForm()
      tipoAgendamentoDataLocale.value = new TipoAgendamentoModel()
    }

    function resetDataAndCloseDialog() {
      resetData()
      props.closeDialog()
    }

    async function criarTipoDeAgendamento() {
      try {
        loading.value = true
        await TipoAgendamentoService.criarTipoDeAgendamento(tipoAgendamentoDataLocale.value)
        emit('refetch-data')
        resetDataAndCloseDialog()
      } finally {
        loading.value = false
      }
    }

    async function atualizarTipoDeAgendamento() {
      try {
        loading.value = true
        await TipoAgendamentoService.atualizarTipoDeAgendamento(props.tipoAgendamentoId, tipoAgendamentoDataLocale.value)
        emit('refetch-data')
        resetDataAndCloseDialog()
      } finally {
        loading.value = false
      }
    }

    async function handleSubmit() {
      if (loading.value) return
      if (formRef.value.validate()) {
        if (isTipoAgendamentoNovo.value) {
          await criarTipoDeAgendamento()
        } else {
          await atualizarTipoDeAgendamento()
        }
      }
    }

    provide('tipoAgendamentoData', tipoAgendamentoDataLocale)

    return {
      loading,
      formRef,
      handleSubmit,
      resetDataAndCloseDialog,
      tipoAgendamentoDataLocale,
      isTipoAgendamentoNovo,
      filas,

      validators: { required, select },
    }
  },
}
</script>
